import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Store } from '@ngrx/store';
import { Observable, catchError, finalize, map, of, switchMap } from 'rxjs';
import { OverlaySpinnerService } from '../../../shared/components/overlay-spinner/overlay-spinner.service';
import { AppState } from '../../../store/global/app.reducer';
import { setActiveUser } from '../../../store/global/user/actions/profile-page.actions';
import { User } from '../../../store/global/user/user.entity';
import { UserFacade } from '../../../store/global/user/user.facade';
import { AppRoute } from '../../constants/app-route.constant';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  constructor(
    private store: Store<AppState>,
    private userFacade: UserFacade,
    private msalService: MsalService,
    private router: Router,
    private spinner: OverlaySpinnerService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    this.spinner.show();
    return this.userFacade.user$.pipe(
      map((user: User) => {
        this.store.dispatch(setActiveUser({ user }));
        return true;
      }),
      catchError((error: HttpErrorResponse) => {
        let result: Observable<boolean | UrlTree>;

        if (this.getResolvedUrl(route) !== 'profile') {
          result = of(
            this.router.createUrlTree([AppRoute.root, AppRoute.profile])
          );
        } else {
          result = of(true);
        }

        if (error.status === HttpStatusCode.NotFound) {
          return this.userFacade.contractorAgreementURL$.pipe(
            switchMap((contractorAgreementURL: string) => {
              const user = User.fromAccountInfo(
                this.msalService.instance.getActiveAccount() ??
                  this.msalService.instance.getAllAccounts()[0]
              );
              user.contractorAgreementUrl = contractorAgreementURL;
              this.store.dispatch(setActiveUser({ user }));
              return result;
            })
          );
        }

        return result;
      }),
      finalize(this.spinner.hide)
    );
  }

  getResolvedUrl(route: ActivatedRouteSnapshot): string {
    const segments = route.pathFromRoot.map((paths) =>
      paths.url.map((segment) => segment.toString())
    );
    return segments[segments.length - 1].join('');
  }
}
